import React from 'react';
import styled from 'styled-components';

import { CopyInput } from '~/components';
import { FormLabel, Inner } from '~/styles';

// Shared top-of-page header frame for single transfers and transfer batches (ACH + check). Owns the
// wrapper padding, the content/ID two-column layout, and the ID field treatment so every header shares
// the exact same spacing and alignment; each caller supplies its own amount + meta content as children.
const Wrapper = styled.div`
  container-type: inline-size;
  container-name: transfer-summary;
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;

  > * {
    min-width: 0;
  }

  @container transfer-summary (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    grid-template-columns: auto min-content;
    gap: 16px;
    // Center the ID field vertically against the content column.
    align-items: center;
  }

  @container transfer-summary (min-width: ${({ theme }) => theme.containerBreakpoints.desktop}) {
    grid-template-columns: minmax(0, 1fr) minmax(310px, min-content);
  }
`;

const IdField = styled.div`
  width: 100%;
  min-width: 180px;
  box-sizing: border-box;
  height: fit-content;
  display: grid;
  grid-template-columns: min-content auto;
  align-items: center;
  align-self: center;
  gap: 8px;

  border-radius: 8px;
  padding: 4px 4px 4px 12px;
  box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(200)};

  @container transfer-summary (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    box-shadow: none;
    margin-bottom: 0;

    display: grid;
    grid-template-columns: auto;
    border: 0;
    gap: 8px;
    padding: 0;
  }
`;

const StyledFormLabel = styled(FormLabel)`
  white-space: nowrap;
`;

const StyledCopyInput = styled(CopyInput)`
  --input-border-color: transparent;

  &:hover {
    --input-background: ${({ theme }) => theme.style.inputDefaultBackgroundColor};
    --input-border-color: ${({ theme }) => theme.style.inputDefaultHoveredBorderColor};
  }

  @container transfer-summary (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    --input-border-color: inherit;
  }
`;

const Action = styled.div`
  display: flex;
  align-items: flex-end;
  margin-bottom: 6px;
`;

interface TransferHeaderFrameProps {
  /** Label shown above the ID copy input, e.g. "Transfer ID" or "Batch ID". */
  idLabel: string;
  /** ID value to display; the field is omitted when absent. */
  idValue?: string;
  /** Optional trailing action (e.g. a button) rendered in the header row. */
  action?: React.ReactNode;
  className?: string;
  /** The left-hand content column: the amount block and the meta row. */
  children: React.ReactNode;
}

export const TransferHeaderFrame: React.FC<TransferHeaderFrameProps> = ({
  idLabel,
  idValue,
  action,
  className,
  children,
}) => (
  <Inner px="var(--page-margin)" pt={20} pb={16} className={className}>
    <Wrapper>
      <Summary>
        {children}

        {idValue && (
          <IdField>
            <StyledFormLabel>{idLabel}</StyledFormLabel>
            <StyledCopyInput value={idValue} size="small" isMonospaced />
          </IdField>
        )}

        {action && <Action>{action}</Action>}
      </Summary>
    </Wrapper>
  </Inner>
);
